import { useSEO } from "@/lib/seo";
import { Link } from "react-router-dom";

const H = ({ children }: { children: React.ReactNode }) => (
  <h2 className="font-heading font-bold text-white text-lg mt-10 mb-3">{children}</h2>
);

export default function Privacy() {
  useSEO("Privacy Policy", "How NewsLayer collects, uses and protects your data.");
  return (
    <div data-testid="privacy-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-4">Legal</p>
      <h1 className="font-heading font-black text-white text-3xl sm:text-5xl">Privacy Policy</h1>
      <p className="font-mono text-xs text-gray-500 mt-3">Last updated: June 2026</p>

      <div className="text-gray-400 text-[15px] leading-relaxed mt-8 space-y-4">
        <p>
          NewsLayer.com ("NewsLayer", "we", "us") is a crypto, Web3 and policy news platform. This policy
          explains what we collect, why, and the choices you have. The short version: we collect the minimum
          needed to run the site, we never sell your personal data, and you can delete your account at any time.
        </p>

        <H>1. What we collect</H>
        <ul className="list-disc pl-5 space-y-2">
          <li><span className="text-gray-200 font-medium">Account data</span> — name, email address and a hashed password when you create an account.</li>
          <li><span className="text-gray-200 font-medium">Newsletter data</span> — your email address when you subscribe to our newsletters or the premium Regulation Radar letter.</li>
          <li><span className="text-gray-200 font-medium">Preferences</span> — coins you watch, language choice, saved feeds, poll votes and reading preferences, stored against your account or in your browser's local storage.</li>
          <li><span className="text-gray-200 font-medium">Usage analytics</span> — anonymous article views and aggregate traffic patterns used to rank trending stories and price advertising. We do not build individual behavioural profiles.</li>
          <li><span className="text-gray-200 font-medium">Push subscriptions</span> — if you enable notifications, your browser's push endpoint so we can deliver alerts. No message content is stored by third parties.</li>
          <li><span className="text-gray-200 font-medium">Payment data</span> — payments for Premium, PR publication, the author program and advertising are processed by Stripe. We never see or store your full card number; we keep only the transaction reference, amount and status.</li>
        </ul>

        <H>2. How we use it</H>
        <ul className="list-disc pl-5 space-y-2">
          <li>To operate your account, watchlists, alerts and premium access.</li>
          <li>To send newsletters and transactional emails (receipts, campaign updates) you've requested — you can unsubscribe from marketing emails at any time.</li>
          <li>To measure aggregate readership, rank trending stories and report anonymous impression/click counts to advertisers.</li>
          <li>To detect abuse and keep the platform secure.</li>
        </ul>

        <H>3. Who we share it with</H>
        <p>We share data only with the processors required to run NewsLayer:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li><span className="text-gray-200 font-medium">Stripe</span> — payment processing.</li>
          <li><span className="text-gray-200 font-medium">Resend</span> — email delivery.</li>
          <li><span className="text-gray-200 font-medium">Cloud hosting & storage providers</span> — running the site and storing uploaded media.</li>
          <li><span className="text-gray-200 font-medium">AI providers</span> — article text (never your personal data) is processed to generate summaries, translations and audio briefs.</li>
        </ul>
        <p>We never sell personal data, and we do not run third-party ad-tracking networks. Sponsored placements on NewsLayer are served first-party and clearly labeled "Ad" or "Sponsored".</p>

        <H>4. Cookies & local storage</H>
        <p>
          We use a session token to keep you signed in and local storage for preferences such as language,
          dismissed banners, watchlists and poll participation. We do not use cross-site tracking cookies.
        </p>

        <H>5. Your rights</H>
        <p>
          Depending on where you live (including under GDPR and CCPA), you may have the right to access, correct,
          export or delete your personal data, and to object to or restrict processing. To exercise any of these
          rights, email us — we respond within 30 days. Deleting your account removes your personal data from
          our active systems.
        </p>

        <H>6. Data retention & security</H>
        <p>
          Account data is kept while your account is active. Anonymous analytics are kept in aggregate. Passwords
          are stored hashed, transport is encrypted (HTTPS), and access to production data is restricted.
        </p>

        <H>7. Children</H>
        <p>NewsLayer is not directed at children under 16 and we do not knowingly collect their data.</p>

        <H>8. Changes & contact</H>
        <p>
          We'll update this page when the policy changes and revise the date above. Questions or requests:{" "}
          <a href="mailto:newslayerapp@gmail.com" className="text-[#00E5FF] hover:underline">newslayerapp@gmail.com</a>.
          See also our <Link to="/terms" className="text-[#00E5FF] hover:underline">Terms of Service</Link>.
        </p>
      </div>
    </div>
  );
}
